import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, FileUp, Info, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Combobox } from '../../components/ui/Combobox';
import { DatePicker, formatDateLabel } from '../../components/ui/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { formatMb, validateHtmlUpload } from '../../lib/hostedReport';
import {
  buildHostedReportPatch,
  describeHostedReportChanges,
  formFromReport,
  hasChanges,
  isClosingAnOpenLink,
  passwordModeOptions,
  PORTAL_PASSWORD_LINK_ONLY_NOTE,
  type HostedReportEditForm,
  type HostedReportPatch,
  type PasswordMode,
} from '../../lib/hostedReportEdit';
import { portalRowAction } from '../../lib/portalLibrary/staffView';
import {
  HOSTED_REPORT_MAX_BYTES,
  type HostedReportSummary,
} from '../../types/hostedReport';
import type { Account } from '../../types/account';

// Tools → HTML Hosting — the edit dialog.
//
// ⚠️ THE TOKEN AND URL NEVER CHANGE. Everything here edits what sits BEHIND a
// link clients already hold, which is why the confirmation step exists: the AM
// is changing something live, not drafting.
//
// The expiry control lives HERE and nowhere else — the standalone "extend"
// action is gone, because two ways to change one field is two sets of rules to
// keep in step.

export function EditHostedReportDialog({
  report,
  accounts,
  busy,
  errorText,
  onSave,
  onClose,
  onAddToPortal,
  onRemoveFromPortal,
}: {
  report: HostedReportSummary;
  accounts: Account[];
  busy: boolean;
  errorText: string | null;
  onSave: (patch: HostedReportPatch) => void;
  onClose: () => void;
  /**
   * Round 2c: opens the SAME "Add to Kyck portal" dialog the list row does.
   * Absent = no button.
   */
  onAddToPortal?: () => void;
  /**
   * Round 6 §1: when the report carries a portal marker the button is "Remove
   * from portal" instead, opening the SAME confirm the list row uses. Absent =
   * no button in that state.
   */
  onRemoveFromPortal?: () => void;
}) {
  const [form, setForm] = useState<HostedReportEditForm>(() => formFromReport(report));
  const [fileError, setFileError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accountOptions = useMemo(
    () => [
      { value: '', label: 'No client account' },
      ...accounts
        .map((a) => ({ value: a.id, label: a.name, description: a.accountType }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [accounts],
  );
  const accountLabel = useMemo(
    () => (id: string) => accounts.find((a) => a.id === id)?.name ?? id,
    [accounts],
  );

  const patch = buildHostedReportPatch(report, form);
  const dirty = hasChanges(patch);
  const changeLines = describeHostedReportChanges(report, patch, accountLabel);

  async function onPickFile(f: File | undefined): Promise<void> {
    setFileError(null);
    setForm((prev) => ({ ...prev, file: null }));
    if (!f) return;
    // The SAME shared rules the server enforces, so a zip is refused before the
    // AM waits on an upload — and the existing file is never touched.
    const check = validateHtmlUpload({
      filename: f.name,
      contentType: f.type,
      byteLength: f.size,
    });
    if (!check.ok) {
      setFileError(check.message);
      return;
    }
    const html = await f.text();
    setForm((prev) => ({ ...prev, file: { filename: f.name, html } }));
  }

  // ---------- confirmation ----------
  if (confirming) {
    return (
      <Dialog open onOpenChange={(open) => !open && !busy && setConfirming(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Save these changes?</DialogTitle>
            {/*
              ⚠️ The load-bearing sentence. This URL is already out there, so the
              AM must not be able to think of this as a draft they will publish
              later — there is no publish step, and no cache to wait out (the
              report is served no-store, which is exactly why).
            */}
            <DialogDescription>
              This link has already been shared. Anyone holding it sees these changes
              immediately — there’s no separate publish step.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1.5 text-sm text-slate-700">
            {changeLines.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          {errorText && <p className="text-sm text-destructive">{errorText}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              Back
            </Button>
            <Button onClick={() => onSave(patch)} disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ---------- edit form ----------
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit report</DialogTitle>
          <DialogDescription>
            The link stays the same — <span className="font-mono text-xs">{report.url}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="hre-title">Title</Label>
            <Input
              id="hre-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hre-account">Client account</Label>
            <Combobox
              id="hre-account"
              value={form.accountId}
              onValueChange={(v) => setForm({ ...form, accountId: v })}
              options={accountOptions}
              placeholder="No client account"
              searchPlaceholder="Search accounts…"
              aria-label="Client account"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hre-expiry">Expires</Label>
            {/*
              ⚠️⚠️ A PORTAL-LINKED report has NO expiry control (Amendment 2 §A):
              its expiry follows the library tile's end date, and the server
              refuses an independent change. A line saying where to change it
              replaces the control, rather than a disabled copy of the date.
            */}
            {report.portalLibraryEntryId !== null ? (
              <p className="text-sm text-slate-700">
                {report.expiresAt === null
                  ? 'Never expires'
                  : `Expires ${formatDateLabel(report.expiresAt.slice(0, 10))}`}{' '}
                — this report is on the Kyck portal, and its expiry follows the tile’s end date.
                Change it on the{' '}
                <a className="underline" href="/tools/portal-library">
                  Portal library
                </a>{' '}
                page.
              </p>
            ) : (
            <>
            {!form.neverExpires && (
              <DatePicker
                id="hre-expiry"
                value={form.expiryDate}
                onChange={(iso) => setForm({ ...form, expiryDate: iso })}
                ariaLabel="Expiry date"
              />
            )}
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={form.neverExpires}
                onCheckedChange={(v) => setForm({ ...form, neverExpires: v === true })}
                aria-label="Never expire"
              />
              Never expire
            </label>
            {/*
              No `minIso` here, unlike create: shortening an expiry — including
              into the past — is how you stop a link working without revoking it.
            */}
            <p className="text-xs text-slate-500">
              {form.neverExpires
                ? 'The link keeps working until you revoke it.'
                : 'You can bring this forward as well as push it back. A date in the past stops the link working straight away.'}
            </p>
            </>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hre-password-mode">Password</Label>
            {/*
              ⚠️⚠️ Round 4 (amended §2): the control STAYS for a portal-linked
              report, labelled for the one door it guards. The shareable link is
              permanent now, and the password is the only control that link has;
              the portal door never asks for it. Hiding it would leave the link
              with no protection anyone could set.
            */}
            {report.portalLibraryEntryId !== null && (
              <p className="text-xs text-slate-600" data-testid="password-link-only-note">
                {PORTAL_PASSWORD_LINK_ONLY_NOTE}
              </p>
            )}
            <SelectField
              value={form.passwordMode}
              onValueChange={(v) =>
                setForm({ ...form, passwordMode: v as PasswordMode, passwordValue: '' })
              }
              options={passwordModeOptions(report.hasPassword)}
              aria-label="Password"
            />
            {form.passwordMode === 'set' && (
              <Input
                id="hre-password"
                type="text"
                value={form.passwordValue}
                onChange={(e) => setForm({ ...form, passwordValue: e.target.value })}
                placeholder="New password"
                aria-label="New password"
              />
            )}
            {/*
              We hold a scrypt hash, not the password — so there is nothing to
              show, and saying so is better than an empty box the AM reads as
              "there isn't one".
            */}
            {report.hasPassword && form.passwordMode === 'keep' && (
              <p className="flex items-start gap-1.5 text-xs text-slate-500">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  This report has a password. We only store a hashed copy, so it can’t
                  be shown — set a new one if it’s been lost.
                </span>
              </p>
            )}
            {form.passwordMode !== 'keep' && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  Anyone currently reading this report loses access when you save, and
                  will need the new password.
                </span>
              </p>
            )}
            {isClosingAnOpenLink(report, form) && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  This link is currently open — anyone with it can view the report until
                  you save.
                </span>
              </p>
            )}
          </div>

          {report.kind === 'bundle' ? (
            <p className="flex items-start gap-1.5 text-xs text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                This is a zip bundle: its files cannot be replaced in place. Upload a new bundle (the
                title, expiry and password above can still be changed).
              </span>
            </p>
          ) : (
          <div className="space-y-1.5">
            <Label htmlFor="hre-file">Replace the file</Label>
            <input
              ref={inputRef}
              id="hre-file"
              type="file"
              accept=".html,.htm,text/html"
              className="sr-only"
              onChange={(e) => void onPickFile(e.target.files?.[0])}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={() => inputRef.current?.click()}
            >
              <FileUp className="h-4 w-4" />
              {form.file ? form.file.filename : 'Choose a new file…'}
            </Button>
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
            <p className="flex items-start gap-1.5 text-xs text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                Optional — leave this alone to keep the current file. One self-contained
                .html file, up to {formatMb(HOSTED_REPORT_MAX_BYTES)}. The previous
                version isn’t kept.
              </span>
            </p>
          </div>
          )}

          {errorText && <p className="text-sm text-destructive">{errorText}</p>}
        </div>

        <DialogFooter>
          {/*
            ⚠️ Round 6 §1: the SAME choice as the list row — `portalRowAction` on
            the report's own marker. With a pointer it is "Remove from portal"
            (which also repairs a stranded marker) and opens the row's confirm;
            without one it is the add, not gated further: the server's refusal
            is the authority there.
          */}
          {portalRowAction(report.portalLibraryEntryId).action === 'remove' ? (
            onRemoveFromPortal && (
              <Button
                variant="ghost"
                className="mr-auto"
                onClick={onRemoveFromPortal}
                disabled={busy}
                title={dirty ? 'Your unsaved changes here will be discarded.' : undefined}
              >
                {dirty
                  ? 'Discard changes and remove from portal'
                  : portalRowAction(report.portalLibraryEntryId).label}
              </Button>
            )
          ) : (
            <>
              {onAddToPortal && (
                // ⚠️ Leaves this dialog: unsaved edits here are not carried over,
                // and the label says so rather than letting them vanish silently.
                <Button
                  variant="ghost"
                  className="mr-auto"
                  onClick={onAddToPortal}
                  disabled={busy}
                  title={dirty ? 'Your unsaved changes here will be discarded.' : undefined}
                >
                  {dirty ? 'Discard changes and add to Kyck portal' : 'Add to Kyck portal'}
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button disabled={!dirty || busy} onClick={() => setConfirming(true)}>
            Review changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Re-exported for the list page's expiry column. */
export { formatDateLabel };
